import type {
  NotificationChannelState,
  NotificationRuleView,
  NotificationSettingsView,
  TemplateChannel,
} from "@planerius/contracts/cabinet-notifications"

/**
 * Матрица уведомлений 4-Б5 — слова и правила показа, отдельно от разметки.
 *
 * ПОЧЕМУ ПОДПИСИ СТРОК СОБИРАЮТСЯ ЗДЕСЬ, А НЕ ПРИХОДЯТ С СЕРВЕРА. Интерфейс переводится
 * (П1-26), а сервер языка кабинета не знает: он отдаёт факты — событие, смещение, класс.
 * Название «Напоминание за сутки» — это слово кабинета, и жить оно обязано там, где
 * лежат остальные слова, иначе перевод придётся делать дважды и в двух местах.
 *
 * ЗАЧЕМ ВООБЩЕ ОТДЕЛЬНЫЙ ФАЙЛ. Главные правила этого экрана словесные: какая строка
 * запирается замком, что написано в неактивной клетке, и почему клетка неактивна.
 * Проверить их тестом на функции можно, а на разметке — только глазами.
 */

/** Подписи событий. Ключ — событие контракта, а не номер строки макета. */
const EVENT_LABELS: Readonly<Record<string, string>> = {
  "appointment.created": "Запись создана",
  "appointment.reminder": "Напоминание",
  "appointment.rescheduled": "Запись перенесена",
  "appointment.cancelled": "Запись отменена",
  "appointment.confirm_request": "Просьба подтвердить запись",
  "appointment.no_show": "Клиент не пришёл",
  "review.request": "Отзыв после визита",
  "client.birthday": "День рождения",
  "client.dormant": "«Давно не были»",
}

/** Подписи под названием: то, что салон обязан знать про эту строку. */
const EVENT_HINTS: Readonly<Record<string, string>> = {
  "appointment.created": "клиенту — подтверждение, что он записан",
  "appointment.cancelled": "отключить нельзя: иначе клиент приедет к закрытой двери",
  "appointment.rescheduled": "отключить нельзя: клиент придёт к старому времени",
  "appointment.no_show": "клиенту — «ждём в следующий раз»",
  "client.birthday": "только по согласию на рекламу",
  "client.dormant": "только по согласию на рекламу",
}

const KIND_WORDS: Readonly<Record<string, string>> = {
  transactional: "служебное",
  service: "сервисное",
  marketing: "рекламное",
}

const CHANNEL_WORDS: Readonly<Record<TemplateChannel, string>> = {
  telegram: "Бот Telegram",
  max: "Бот MAX",
  push: "Пуш в приложении",
  email: "Почта",
  sms: "SMS",
}

export interface MatrixRow {
  readonly id: string
  readonly label: string
  readonly hint: string | null
  /**
   * Чего не хватает, чтобы строка заработала. null — работает.
   *
   * ПРИХОДИТ С СЕРВЕРА, а не считается здесь: «есть ли источник события» — это состояние
   * продукта, и знает его тот, кто события порождает. Строка с непустым признаком стоит
   * выключенной и объясняет причину: включённый переключатель, который ничего не делает,
   * хуже выключенного — салон ждёт сообщений, которых не будет.
   */
  readonly pendingReason: string | null
  readonly kindWord: string
  readonly enabled: boolean
  /** Замок: событие обязательное, галочку снять нельзя. */
  readonly locked: boolean
  /**
   * Клетки по столбцам — в том же порядке, что и столбцы.
   *
   * ВСЕ КЛЕТКИ, А НЕ ТОЛЬКО ВЫБРАННЫЕ: неподключённый канал в макете ВИДЕН и подписан
   * «канал не подключён» — иначе салон не узнает, что бота можно подключить и получать
   * напоминания бесплатно.
   */
  readonly cells: readonly MatrixCell[]
}

export interface MatrixCell {
  readonly channel: TemplateChannel
  readonly channelWord: string
  /** Канал выбран для этой строки. */
  readonly on: boolean
  /** Нажать нельзя: канал не подключён либо строка выключена целиком. */
  readonly disabled: boolean
  /** Почему нельзя или что означает отметка. Уходит в title и в aria-label. */
  readonly note: string
}

/**
 * Пустой перечень каналов у правила означает «все пригодные».
 *
 * ЭТО НЕ УДОБСТВО ХРАНЕНИЯ, А СМЫСЛ: правило по умолчанию не перечисляет каналы, потому
 * что каскад сам выбирает дешёвый. Экран обязан показать такую строку отмеченной
 * по всем подключённым каналам — иначе салон видит десять пустых клеток и решает,
 * что не уходит ничего.
 */
export function cellOn(rule: NotificationRuleView, channel: TemplateChannel): boolean {
  return rule.channels.length === 0 ? true : rule.channels.includes(channel)
}

export function matrixRows(settings: NotificationSettingsView): readonly MatrixRow[] {
  return settings.rules.map((rule) => {
    const base = EVENT_LABELS[rule.event] ?? rule.event
    const label =
      rule.offsetMinutes === null
        ? base
        : `${base} ${offsetWords(rule.offsetMinutes)}`
    return {
      id: rule.id,
      label,
      hint: EVENT_HINTS[rule.event] ?? null,
      pendingReason: rule.pendingReason,
      kindWord: KIND_WORDS[rule.kind] ?? rule.kind,
      // Строка без источника показывается ВЫКЛЮЧЕННОЙ, чем бы ни было записано в базе:
      // её положение в базе ни на что не влияет, а показать «включено» значит обещать
      // сообщения, которых никто не создаёт.
      enabled: rule.enabled && rule.pendingReason === null,
      locked: rule.mandatory || rule.pendingReason !== null,
      cells: settings.channels.map((channel) =>
        cellOf(
          rule.pendingReason === null ? rule : { ...rule, enabled: false },
          channel,
        ),
      ),
    }
  })
}

function cellOf(rule: NotificationRuleView, channel: NotificationChannelState): MatrixCell {
  const word = CHANNEL_WORDS[channel.channel] ?? channel.channel
  if (!channel.configured) {
    return {
      channel: channel.channel,
      channelWord: word,
      on: false,
      disabled: true,
      note: channel.note ?? "Канал пока не подключён",
    }
  }
  if (!rule.enabled) {
    return {
      channel: channel.channel,
      channelWord: word,
      on: false,
      disabled: true,
      // Выключенная строка — не то же самое, что снятая клетка: включив событие обратно,
      // салон вернёт свои каналы. Об этом надо сказать, иначе он снимет и клетки тоже.
      note: "Событие выключено целиком — включите его, чтобы выбрать каналы",
    }
  }
  const on = cellOn(rule, channel.channel)
  return {
    channel: channel.channel,
    channelWord: word,
    on,
    disabled: false,
    note: on ? `${word}: включено` : `${word}: выключено`,
  }
}

/** «за сутки», «за 2 часа», «за 15 минут» — так, как это сказал бы человек. */
export function offsetWords(minutes: number): string {
  if (minutes % (24 * 60) === 0) {
    const days = minutes / (24 * 60)
    return days === 1 ? "за сутки" : `за ${String(days)} суток`
  }
  if (minutes % 60 === 0) {
    const hours = minutes / 60
    const word = hours === 1 ? "час" : hours < 5 ? "часа" : "часов"
    return `за ${String(hours)} ${word}`
  }
  return `за ${String(minutes)} мин`
}

/**
 * Что написать над матрицей.
 *
 * ГЛАВНОЕ СООБЩЕНИЕ ЭКРАНА — НЕ НАСТРОЙКА, А КАСКАД: «сначала бот, не доставлено — пуш,
 * потом почта, и только в конце SMS». Без этой фразы матрица читается как «выбери каналы»,
 * и салон ставит галочки во все пять, полагая, что так надёжнее, — а получает пять
 * сообщений об одном визите и счёт за SMS.
 */
export const MATRIX_CASCADE_NOTE =
  "Отправляем по цепочке от бесплатного к платному: сначала бот, не доставлено — пуш, потом почта, и только в конце SMS. Одно сообщение уходит ОДНИМ каналом."

/**
 * Шапка столбца: имя канала словами и приписка про цену.
 *
 * «telegram» в шапке таблицы человек читает как имя переменной, а не как канал.
 * Приписка про цену стоит здесь же: столбец SMS обязан выглядеть дороже остальных
 * ДО того, как в него поставили галочку.
 */
export function channelHead(channel: NotificationChannelState): {
  readonly label: string
  readonly sub: string
} {
  return {
    label: CHANNEL_WORDS[channel.channel] ?? channel.channel,
    sub: channel.configured ? (channel.free ? "бесплатно" : "платный") : "не подключён",
  }
}

/** Строка о состоянии каналов: сколько из пяти работает. Пустая, если работают все. */
export function channelsWarning(settings: NotificationSettingsView): string | null {
  const missing = settings.channels.filter((channel) => !channel.configured)
  if (missing.length === 0) return null
  if (missing.length === settings.channels.length) {
    return "Пока не подключён ни один канал: сообщения планируются, но не уходят. Матрицу можно настроить заранее — она заработает вместе с первым подключённым каналом."
  }
  return `Не подключено каналов: ${String(missing.length)}. Их столбцы видны, но неактивны.`
}
